'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getContainer } from '@framework/di/getContainer';
import debounce from 'lodash/debounce';
import { SortOrder } from '@dealroadshow/uikit-table/DataTable/interfaces/SortOrder';
import useFetchCollection from '@framework/state/useFetchCollection';
import { IDataroomListItem } from '../domain/vo/DataroomListItem';
import RestrictedRepository from '../infrastructure/RestrictedRepository';
import TransactionTypeGroupsContext, {
  useTransactionTypeGroupsContext,
} from './TransactionTypeGroupsContext';
import SectorContext, { useSectorContext } from '@dm-portal/sector/SectorContext';
import IndustryContext, { useIndustryContext } from './IndustryContext';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import config from '@dataroom/config';
import DataroomErrorHandler from '@dataroom/error-handler';

const perPage: number = 100;
const sortOrder: SortOrder = 'asc';
let sortBy: string = 'sponsor';

type RestrictedListPayload = { groupId: string; isPublicRequest: boolean };

const useRestrictedListing = (isPublicRequest: boolean) => {
  const container = getContainer();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isCountsFetching, setCountsFetching] = useState(false);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [allCounts, setAllCounts] = useState({});

  const { getSectorList } = useSectorContext();
  const { getIndustryList } = useIndustryContext();
  const { tenant } = useDataroomTenantContext();

  if (tenant === config.verisend.code || tenant === config.researchroom.code) {
    sortBy = 'name';
  }

  const {
    activeGroup,
    isEmptyCollection,
    getTransactionTypeGroups,
    collection,
  } = useTransactionTypeGroupsContext();

  const groupIds = collection.map((group) => group.id);
  useEffect(() => {
    Promise.all([getSectorList(), getIndustryList(), getTransactionTypeGroups()])
      .then(() => {
        setIsInitialized(true);
      });
  }, []);

  useEffect(() => {
    activeGroup && setGroupId(activeGroup.id);
  }, [activeGroup]);

  const restrictedRepository = container.get<RestrictedRepository>(RestrictedRepository);

  const getCounts = useCallback(debounce(async (search: string): Promise<void> => {
    const payload = { search };
    try {
      const counts = await restrictedRepository.getRestrictedTotalCount(payload, isPublicRequest);
      setAllCounts(counts);
    } catch (error) {
      container.get(DataroomErrorHandler)
        .handleError(error);
    } finally {
      setCountsFetching(false);
    }
  }, 500), [groupIds]);

  const fetchCollection = useFetchCollection<IDataroomListItem, RestrictedListPayload>(
    restrictedRepository.getRestrictedList as Parameters<typeof useFetchCollection<IDataroomListItem, RestrictedListPayload>>[0],
    {
      enabled: isInitialized && !!groupId,
      payload: {
        groupId: groupId!,
        isPublicRequest,
      },
      initialValues: {
        perPage,
        sortBy,
        sortOrder,
      },
    },
  );

  return {
    ...fetchCollection,
    isFetching: fetchCollection.isFetching || !groupId || isCountsFetching,
    isZeroCase: fetchCollection.isZeroCase || isEmptyCollection,
    groupId,
    setGroupId,
    getCounts,
    allCounts,
  };
};

type RestrictedListingContextType = ReturnType<typeof useRestrictedListing>;

export const RestrictedListingContext = createContext<RestrictedListingContextType | null>(null);

export const useRestrictedListingContext = () => {
  const context = useContext(RestrictedListingContext);
  if (!context) {
    throw new Error('useRestrictedListingContext must be used within a RestrictedListingContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
  isPublicRequest: boolean,
}

const RestrictedListingContextProvider = ({
  children,
  isPublicRequest,
}: IProps) => (
  <RestrictedListingContext.Provider value={ useRestrictedListing(isPublicRequest) }>
    { children }
  </RestrictedListingContext.Provider>
);

export default ({
  children,
  isPublicRequest,
}: IProps) => (
  <SectorContext>
    <IndustryContext>
      <TransactionTypeGroupsContext>
        <RestrictedListingContextProvider isPublicRequest={ isPublicRequest }>
          { children }
        </RestrictedListingContextProvider>
      </TransactionTypeGroupsContext>
    </IndustryContext>
  </SectorContext>
);
